/**
 * The pilot card: who you are, and what you have to show for it.
 *
 * It stands where the sign-in form stands for everyone else — in the hero, on
 * the same panel, at the same width. Home is the front of the site either way,
 * and signing in should change what the panel says rather than what the page
 * is.
 *
 * Most of what a pilot card wants to say is not recorded anywhere yet. Rather
 * than leave the shape out until it is, every figure is declared in one place
 * (STATS) with whether it is real, and a placeholder draws as an em dash and
 * says so. Search this file for `placeholder: true` to find what is owed.
 *
 * All account text goes in through textContent. A handle is a domain name
 * someone else chose.
 */

import { listMatches, type Session } from "../api";
import { matchesHref } from "../destinations";
import { el } from "../dom";
import { notReadyNote, soonStamp } from "../soon-stamp";
import { accountName, avatarUrl, monogram } from "../profile";

/**
 * What the card counts, in the order it shows them.
 *
 * `read` is what makes a figure real: it is called with the session and
 * resolves to a number. A stat with no `read` is a placeholder, drawn dimmed
 * with an em dash, and nothing is fetched for it.
 *
 * Reading is per-stat and deliberately not batched. Two of these come from two
 * different hosts today, and one being slow or down must not cost the other.
 */
type Stat = {
  readonly label: string;
  /** Absent means nothing records this yet. */
  readonly read?: (session: Session) => Promise<number>;
  /** Why the figure is missing. Placeholders only. */
  readonly placeholder?: string;
};

export const STATS: readonly Stat[] = [
  {
    label: "Camo",
    // Real, and read straight from the player's own repository — the same
    // public listing the editor does, so this costs headquarters nothing.
    read: async (session) => {
      const repo = await import("../camo/repo");
      return (await repo.list(session.did)).length;
    },
  },
  {
    label: "Matches",
    // Real, as far as the control plane's own record of them goes.
    read: async () => (await listMatches()).length,
  },
  // Nothing reports a result at the end of a match yet, so neither of these
  // has a source. They are on the card because the shape of the card is the
  // thing being decided, and a card that only counts what is easy to count
  // would be the wrong shape.
  { label: "Wins", placeholder: "No match result is recorded yet." },
  { label: "Kills", placeholder: "No match result is recorded yet." },
];

/**
 * The card.
 *
 * Every figure starts as an em dash and is filled in when its own read lands,
 * so the card is complete and readable from the first frame and nothing on it
 * moves as the numbers arrive.
 */
export function pilotCard(session: Session): HTMLElement {
  const name = accountName(session);

  const identity = el("div", { className: "pilot-identity" }, [
    avatar(session),
    el("div", { className: "pilot-who" }, [
      el("p", { className: "pilot-name", textContent: name }),
      // The DID under the handle, because the handle is rented and this is not.
      el("p", { className: "pilot-did", textContent: session.did }),
    ]),
  ]);

  return el("section", { className: "pilot" }, [
    // The card names itself for the document outline. On screen the avatar and
    // the handle say the same thing louder, so the heading is not repeated.
    el("h2", { className: "visually-hidden", textContent: "Your pilot card" }),
    identity,
    statGrid(session),
    el("div", { className: "pilot-actions" }, [
      el("a", { className: "pilot-action", href: matchesHref(session) }, [
        "Your matches",
      ]),
      el("a", { className: "pilot-action", href: "/#camo" }, ["Design camo"]),
    ]),
  ]);
}

function statGrid(session: Session): HTMLElement {
  const grid = el(
    "div",
    { className: "pilot-stats" },
    STATS.map((stat) => tile(stat, session)),
  );
  return grid;
}

/** One figure. Em dash until its read lands, and an em dash for good if it never does. */
function tile(stat: Stat, session: Session): HTMLElement {
  const value = el("span", { className: "pilot-value", textContent: "—" });
  const label = el("span", {
    className: "pilot-label",
    textContent: stat.label,
  });
  const box = el("div", { className: "pilot-stat" }, [value, label]);

  if (!stat.read) {
    box.classList.add("pilot-stat-soon");
    // The reason belongs on the tile, not in a legend under the card: a player
    // reading a dash wants to know why that one is a dash.
    box.title = stat.placeholder ?? "Not recorded yet.";
    // The site's one mark for a thing that is named and not built, rather than
    // the word "soon" in the brand teal that used to be here. Same mark as the
    // front page's facts and Play's unbuilt Modes — a dash on this tile means
    // what an empty card over there means, and it should not take a second
    // vocabulary to say so. The caption carries the note for a screen reader.
    label.append(notReadyNote());
    box.append(soonStamp("tile"));
    return box;
  }

  stat
    .read(session)
    .then((count) => {
      value.textContent = String(count);
    })
    .catch((error: unknown) => {
      // A figure that cannot be read stays an em dash. Nothing on this card is
      // worth an error screen, and three of the four may still have arrived.
      console.warn(`pilot: ${stat.label} could not be counted`, error);
      box.title = "This could not be read just now.";
    });

  return box;
}

/**
 * The avatar, or the initial it stands in for.
 *
 * The same two-step the masthead's does — monogram first, picture once it has
 * decoded — because it is the same full-size blob off the same PDS, and here
 * it is drawn four times as large.
 */
function avatar(session: Session): HTMLElement {
  const box = el("span", { className: "avatar pilot-avatar" }, [
    el("span", {
      className: "avatar-monogram",
      textContent: monogram(session),
    }),
  ]);

  void avatarUrl(session.did)
    .then((src) => {
      if (!src) return;
      const image = el("img", {
        className: "avatar-image",
        src,
        // Decorative: the handle is beside it.
        alt: "",
        decoding: "async",
      });
      image.addEventListener("load", () => box.replaceChildren(image));
    })
    .catch((error: unknown) => {
      console.warn("pilot: the avatar could not be found", error);
    });

  return box;
}
